import React, { useState, useEffect } from "react";
import { X, Search, Copy, Check, FolderOpen } from "lucide-react";
import { getAllIcons, IIconBucket } from "@/api/admin/iconBucket";
import { FileTypeIcon } from "@/components/molecules/FileTypeIcon";
import { getFileExtension, resolveFileTypeDescriptor } from "@/constants/fileUpload";
import { toast } from "@/hooks/useToast";

interface IconBucketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  currentValue?: string;
  filterCategory?: "images" | "icons" | "all";
}

// Category filter configuration
const CATEGORY_FILTERS = [
  { value: "all", label: "All" },
  { value: "images", label: "Images" },
  { value: "icons", label: "Icons" },
] as const;

// Button style configurations
const BUTTON_STYLES = {
  selected: "bg-blue-600 text-white shadow-sm",
  unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
} as const;

// Card border style configurations
const CARD_BORDER_STYLES = {
  selected: "border-blue-600 ring-2 ring-blue-200",
  unselected: "border-gray-200 hover:border-blue-300",
} as const;

// Empty state configurations
const EMPTY_STATE_CONFIG = {
  filtered: {
    title: "No items found matching your criteria",
    description: "Try adjusting your filters",
  },
  empty: {
    title: "No items in the file bucket",
    description: "Add files to get started",
  },
} as const;

export const IconBucketModal: React.FC<IconBucketModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentValue,
  filterCategory = "all",
}) => {
  const [icons, setIcons] = useState<IIconBucket[]>([]);
  const [filteredIcons, setFilteredIcons] = useState<IIconBucket[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<"all" | "images" | "icons">(
    filterCategory
  );
  const [copiedId, setCopiedId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchIcons();
    }
  }, [isOpen]);

  useEffect(() => {
    filterIcons();
  }, [icons, searchQuery, selectedCategory]);

  const fetchIcons = async () => {
    setIsLoading(true);
    try {
      const response = await getAllIcons();
      console.log("Icon Bucket API Response:", response);
      
      if (response?.data) {
        // Backend returns { data: { icons: [...] } }
        let iconsData = [];
        
        if (Array.isArray(response.data)) {
          // If data is directly an array
          iconsData = response.data;
        } else if (response.data.icons && Array.isArray(response.data.icons)) {
          // If data has icons property
          iconsData = response.data.icons;
        }
        
        console.log("Processed icons data:", iconsData);
        setIcons(iconsData);
      }
    } catch (error) {
      console.error("Failed to fetch icons:", error);
      toast({
        title: "Error",
        description: "Failed to load icon bucket",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterIcons = () => {
    // Ensure icons is always an array
    const iconsArray = Array.isArray(icons) ? icons : [];
    let filtered = iconsArray;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((icon) => icon.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (icon) =>
          icon.name.toLowerCase().includes(query) ||
          icon.image_url.toLowerCase().includes(query)
      );
    }

    setFilteredIcons(filtered);
  };

  const handleCopyUrl = (url: string, id: number) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast({
      title: "Copied!",
      description: "URL copied to clipboard",
    });
  };

  const handleSelect = (url: string) => {
    onSelect(url);
    onClose();
  };

  // Helper functions
  const getButtonStyle = (value: string) => {
    return selectedCategory === value
      ? BUTTON_STYLES.selected
      : BUTTON_STYLES.unselected;
  };

  const getCardBorderStyle = (imageUrl: string) => {
    return currentValue === imageUrl
      ? CARD_BORDER_STYLES.selected
      : CARD_BORDER_STYLES.unselected;
  };

  const getEmptyStateContent = () => {
    const hasActiveFilters = searchQuery || selectedCategory !== "all";
    return hasActiveFilters ? EMPTY_STATE_CONFIG.filtered : EMPTY_STATE_CONFIG.empty;
  };

  const getCopyIconComponent = (iconId: number) => {
    return copiedId === iconId ? (
      <Check className="w-4 h-4 text-green-600" />
    ) : (
      <Copy className="w-4 h-4 text-gray-600" />
    );
  };

  const getItemsCountText = () => {
    const count = Array.isArray(filteredIcons) ? filteredIcons.length : 0;
    return `${count} ${count === 1 ? "item" : "items"} found`;
  };

  if (!isOpen) return null;

  const emptyStateContent = getEmptyStateContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">File Bucket</h2>
            <p className="text-sm text-gray-500 mt-1">
              Select a file from your bucket
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          {/* Category Filter */}
          <div className="flex gap-2">
            {CATEGORY_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSelectedCategory(value as any)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${getButtonStyle(value)}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or URL..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : !Array.isArray(filteredIcons) || filteredIcons.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-medium">
                {emptyStateContent.title}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {emptyStateContent.description}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.isArray(filteredIcons) && filteredIcons.map((icon) => {
                const descriptor = resolveFileTypeDescriptor(icon.file_type, icon.image_url);
                const extension = getFileExtension(icon.image_url);

                return (
                <div
                  key={icon.id}
                  className={`group relative bg-white border-2 rounded-xl overflow-hidden transition-all hover:shadow-lg ${getCardBorderStyle(icon.image_url)}`}
                >
                  {/* Preview — real thumbnail for images, type glyph for every
                      other kind of file the bucket now holds */}
                  <div className="aspect-square bg-gray-50 flex flex-col items-center justify-center gap-2 p-4">
                    {descriptor.isPreviewable && descriptor.key === "image" ? (
                      <img
                        src={icon.image_url}
                        alt={icon.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext fill='%239ca3af' x='50%25' y='50%25' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    ) : (
                      <>
                        <FileTypeIcon fileType={descriptor.key} className="w-10 h-10 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          {extension ? `.${extension}` : descriptor.label}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Info Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <p className="font-semibold text-sm truncate">{icon.name}</p>
                      <p className="text-xs opacity-75 mt-0.5 capitalize">
                        {descriptor.label} · {icon.category}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyUrl(icon.image_url, icon.id!);
                      }}
                      className="p-1.5 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                      title="Copy URL"
                    >
                      {getCopyIconComponent(icon.id!)}
                    </button>
                  </div>

                  {/* Select Button */}
                  <button
                    onClick={() => handleSelect(icon.image_url)}
                    className="absolute inset-0 w-full h-full"
                    title={`Select ${icon.name}`}
                  />

                  {/* Selected Indicator */}
                  {currentValue === icon.image_url && (
                    <div className="absolute top-2 left-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">{getItemsCountText()}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
